/**
 * Who may supervise a library.
 *
 * *** WITHOUT THIS SCREEN THE GRANT IS SQL-ONLY ***
 * The API and the join rules have been in place since step 3, but nothing
 * in the portal could create a grant — so a mentor could not be put into a
 * library at all without someone writing a query by hand. The room chat
 * says "no mentor is in right now" because no tenant has ever been able to
 * grant one.
 *
 * *** A GRANT IS NOT A SEAT ***
 * A granted mentor may enter and is counted separately from students; they
 * never consume capacity. Revoking does NOT evict someone already inside —
 * throwing a mentor out of a room mid-conversation would be worse than
 * letting the session finish, and the grant is checked again on their next
 * join.
 */

import { useState } from 'react'
import type { FormEvent } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { SelectField } from '@/components/ui/Field'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import {
  useGrantLibraryMentor,
  useLibraryMentors,
  useMentors,
  useRevokeLibraryMentor,
} from '@/lib/queries'
import type { AdminLibrary } from '@/types/api'
import styles from './LibraryMentorsModal.module.css'

export function LibraryMentorsModal({
  library,
  onClose,
}: {
  library: AdminLibrary | null
  onClose: () => void
}) {
  const libraryId = library?.library_id ?? null
  const { data: granted, isLoading } = useLibraryMentors(libraryId)
  const { data: allMentors } = useMentors('active')
  const grant = useGrantLibraryMentor()
  const revoke = useRevokeLibraryMentor()
  const toast = useToast()

  const [picked, setPicked] = useState('')

  const current = granted?.mentors ?? []
  const grantedIds = new Set(current.map((m) => m.mentor_id))

  /*
    Already-granted mentors are filtered OUT of the picker rather than shown
    disabled. The grant is idempotent server-side, so picking one again would
    silently do nothing — a list that cannot produce a no-op is clearer than
    a toast explaining one.
  */
  const addable = (allMentors?.mentors ?? []).filter(
    (m) => !grantedIds.has(m.user_id),
  )

  const handleGrant = async (event: FormEvent) => {
    event.preventDefault()
    if (!libraryId || !picked) return
    try {
      await grant.mutateAsync({ libraryId, mentorId: picked })
      setPicked('')
      toast.success('Mentor granted access.')
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Could not grant access.',
      )
    }
  }

  const handleRevoke = async (mentorId: string, name: string) => {
    if (!libraryId) return
    try {
      await revoke.mutateAsync({ libraryId, mentorId })
      toast.success(`${name} can no longer enter this library.`)
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Could not revoke access.',
      )
    }
  }

  return (
    <Modal
      open={library !== null}
      onClose={onClose}
      title={library ? `Mentors — ${library.name}` : 'Mentors'}
      description="Granted mentors can enter this library. They never take a student's seat."
      footer={
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className={styles.body}>
        <form onSubmit={handleGrant} className={styles.add}>
          <SelectField
            label="Add a mentor"
            value={picked}
            onChange={(event) => setPicked(event.target.value)}
            placeholder={
              addable.length > 0
                ? 'Choose a mentor…'
                : 'Every active mentor already has access'
            }
            disabled={addable.length === 0 || grant.isPending}
            options={addable.map((m) => ({
              value: m.user_id,
              label: m.full_name,
            }))}
            hint="Only active mentors are listed."
          />
          <Button
            type="submit"
            variant="primary"
            disabled={!picked || grant.isPending}
          >
            {grant.isPending ? 'Granting…' : 'Grant access'}
          </Button>
        </form>

        <div className={styles.listBlock}>
          <h4 className={styles.listTitle}>
            With access
            <span className={styles.count}>{current.length}</span>
          </h4>

          {isLoading && <p className={styles.muted}>Loading…</p>}

          {!isLoading && current.length === 0 && (
            /*
              Names the consequence rather than just the state. "No mentors"
              alone leaves an admin unsure whether that is a problem.
            */
            <p className={styles.muted}>
              No mentor can enter this library yet, so students studying here
              have nobody to ask.
            </p>
          )}

          <ul className={styles.list}>
            {current.map((mentor) => {
              /* `full_name` is nullable — a mentor whose profile row is
                 missing would otherwise render the word "null". */
              const name = mentor.full_name ?? mentor.email
              return (
                <li key={mentor.mentor_id} className={styles.row}>
                  <div className={styles.who}>
                    <span className={styles.whoName}>{name}</span>
                    {mentor.full_name && (
                      <span className={styles.whoMail}>{mentor.email}</span>
                    )}
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleRevoke(mentor.mentor_id, name)}
                    disabled={revoke.isPending}
                  >
                    Revoke
                  </Button>
                </li>
              )
            })}
          </ul>
        </div>

        {/*
          Said once, here, rather than in a confirmation on every revoke: an
          admin who knows the rule does not need to be asked, and a mentor
          mid-conversation is not an emergency.
        */}
        <p className={styles.note}>
          Revoking takes effect the next time the mentor tries to enter.
          Anyone already inside stays until they leave.
        </p>
      </div>
    </Modal>
  )
}
